import React, { useState, useEffect } from 'react';
import { api } from './api/api';

const humanizeKey = (key) => key
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/^./, c => c.toUpperCase());

const isIdField = (key) => /^id$/i.test(key) || /Id$/.test(key);

const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

// ── Small building blocks ────────────────────────────────────────────────

const DetailBlock = ({ title, children }) => (
  <div className="sr-detail-block">
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--c-text-muted)', marginBottom: 6 }}>
      {title}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {children}
    </div>
  </div>
);

const FieldRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
    <span style={{ color: 'var(--c-text-muted)' }}>{label}</span>
    <span style={{ fontWeight: 600, textAlign: 'right' }}>{fmt(value)}</span>
  </div>
);

// ── Car parts: one row per part, "Chassis 7  51% -> 76%" ─────────────────
// Order follows first-appearance in the backend's JSON, not a fixed list —
// requested so the block mirrors whatever order the backend actually sends.

const CAR_PART_DEFS = [
  { match: 'chassis', label: 'Chassis' },
  { match: 'engine', label: 'Engine' },
  { match: 'frontWing', label: 'Front Wing' },
  { match: 'rearWing', label: 'Rear Wing' },
  { match: 'underbody', label: 'Underbody' },
  { match: 'sidepods', label: 'Sidepods' },
  { match: 'cooling', label: 'Cooling' },
  { match: 'gear', label: 'Gearbox' }, // backend field prefix is "gear", not "gearbox"
  { match: 'brakes', label: 'Brakes' },
  { match: 'suspension', label: 'Suspension' },
  { match: 'electronics', label: 'Electronics' },
];

const buildCarPartRows = (race) => {
  const consumed = new Set();
  const byPart = new Map();
  const order = [];
  Object.keys(race).forEach(k => {
    const def = CAR_PART_DEFS.find(d => k.toLowerCase().startsWith(d.match.toLowerCase()));
    if (!def) return;
    consumed.add(k);
    if (!byPart.has(def.match)) {
      byPart.set(def.match, { label: def.label, level: null, start: null, finish: null, extras: [] });
      order.push(def.match);
    }
    const bucket = byPart.get(def.match);
    if (/lvl|level/i.test(k)) bucket.level = race[k];
    else if (/start/i.test(k)) bucket.start = race[k];
    else if (/finish/i.test(k)) bucket.finish = race[k];
    else bucket.extras.push([k, race[k]]);
  });
  const rows = order.map(m => byPart.get(m));
  return { rows, consumed };
};

const CarPartRow = ({ row }) => (
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
      <span style={{ color: 'var(--c-text-muted)' }}>{row.label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right' }}>
        {row.level ?? '—'} &nbsp; {row.start != null ? `${row.start}%` : '—'} → {row.finish != null ? `${row.finish}%` : '—'}
      </span>
    </div>
    {row.extras.map(([k, v]) => (
      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 11, paddingLeft: 10 }}>
        <span style={{ color: 'var(--c-text-light)' }}>{humanizeKey(k)}</span>
        <span style={{ color: 'var(--c-text-light)' }}>{fmt(v)}</span>
      </div>
    ))}
  </div>
);

// ── Energy: one row per session, "Q1: 5% (72 -> 67)" ──────────────────────

const ENERGY_SESSIONS = [
  { label: 'Q1', fromKey: 'energyQ1From', toKey: 'energyQ1To' },
  { label: 'Q2', fromKey: 'energyQ2From', toKey: 'energyQ2To' },
  { label: 'Race', fromKey: 'energyRaceFrom', toKey: 'energyRaceTo' },
];

const buildEnergyRows = (race) => {
  const consumed = new Set();
  const rows = ENERGY_SESSIONS.map(s => {
    const hasFrom = race[s.fromKey] !== undefined;
    const hasTo = race[s.toKey] !== undefined;
    if (!hasFrom && !hasTo) return null;
    if (hasFrom) consumed.add(s.fromKey);
    if (hasTo) consumed.add(s.toKey);
    const from = race[s.fromKey];
    const to = race[s.toKey];
    const used = (from != null && to != null) ? from - to : null;
    return { label: s.label, from, to, used };
  }).filter(Boolean);
  return { rows, consumed };
};

const EnergyRow = ({ row }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
    <span style={{ color: 'var(--c-text-muted)' }}>{row.label}</span>
    <span style={{ fontWeight: 600, textAlign: 'right' }}>
      {row.used != null ? `${row.used}%` : '—'} ({fmt(row.from)} → {fmt(row.to)})
    </span>
  </div>
);

// ── Assemble all blocks for one race ──────────────────────────────────────

const RACE_META_KEYS = ['season', 'raceNumber', 'trackName', 'startPosition', 'finishPosition',
  'weather', 'tyreCompound'];

// "Car Power Base" / "Car Handling Test" / ... -> two blocks, "Car PHA Base"
// and "Car PHA Test", right under "Car PHA".
const CAR_PHA_METRICS = ['power', 'handling', 'accel', 'acceleration'];
const CAR_PHA_VARIANTS = ['Base', 'Test'];

const buildCarPhaVariantBlocks = (race, rendered) => {
  const blocks = [];
  CAR_PHA_VARIANTS.forEach(variant => {
    const keys = Object.keys(race).filter(k =>
      !rendered.has(k) && !isIdField(k) &&
      k.toLowerCase().startsWith('car') &&
      k.toLowerCase().endsWith(variant.toLowerCase()) &&
      CAR_PHA_METRICS.some(m => k.toLowerCase().includes(m)));
    if (keys.length === 0) return;
    keys.forEach(k => rendered.add(k));
    const variantSuffix = new RegExp(`\\s*${variant}$`, 'i');
    blocks.push({ title: `Car PHA ${variant}`, node: (
      <>{keys.map(k => (
        <FieldRow key={k} label={humanizeKey(k).replace(/^Car\s+/i, '').replace(variantSuffix, '')} value={race[k]} />
      ))}</>
    ) });
  });
  return blocks;
};

const buildDetailBlocks = (race) => {
  const rendered = new Set();
  const blocks = [];

  // Race — anomaly flag(s) always right before their reason field(s),
  // regardless of the exact key spelling the backend uses for either.
  const anomalyKeys = Object.keys(race).filter(k => /anomaly/i.test(k) && !isIdField(k));
  const anomalyFlagKeys = anomalyKeys.filter(k => !/reason/i.test(k));
  const anomalyReasonKeys = anomalyKeys.filter(k => /reason/i.test(k));
  const raceFields = [
    ...RACE_META_KEYS.filter(k => race[k] !== undefined),
    ...anomalyFlagKeys,
    ...anomalyReasonKeys,
  ];
  raceFields.forEach(k => rendered.add(k));
  if (raceFields.length > 0) {
    blocks.push({ title: 'Race', node: (
      <>{raceFields.map(k => <FieldRow key={k} label={humanizeKey(k)} value={race[k]} />)}</>
    ) });
  }

  // Driver ("Driver " stripped from labels, driverName -> Manager)
  const driverKeys = Object.keys(race).filter(k => k.toLowerCase().startsWith('driver') && !isIdField(k));
  driverKeys.forEach(k => rendered.add(k));
  if (driverKeys.length > 0) {
    blocks.push({ title: 'Driver', node: (
      <>{driverKeys.map(k => (
        <FieldRow key={k}
          label={k === 'driverName' ? 'Manager' : humanizeKey(k).replace(/^Driver\s+/i, '')}
          value={race[k]} />
      ))}</>
    ) });
  }

  // Car PHA ("Car " stripped from labels)
  const carPhaKeys = Object.keys(race).filter(k =>
    !isIdField(k) && (k === 'carPower' || k === 'carHandling' || k === 'carAcceleration' || k.toLowerCase().includes('pha')));
  carPhaKeys.forEach(k => rendered.add(k));
  if (carPhaKeys.length > 0) {
    blocks.push({ title: 'Car PHA', node: (
      <>{carPhaKeys.map(k => (
        <FieldRow key={k} label={humanizeKey(k).replace(/^Car\s+/i, '')} value={race[k]} />
      ))}</>
    ) });
  }
  blocks.push(...buildCarPhaVariantBlocks(race, rendered));

  // Car levels / wear — one row per part
  const { rows: partRows, consumed: partConsumed } = buildCarPartRows(race);
  partConsumed.forEach(k => rendered.add(k));
  if (partRows.length > 0) {
    blocks.push({ title: 'Car Levels / Wear', node: (
      <>{partRows.map(row => <CarPartRow key={row.part} row={row} />)}</>
    ) });
  }

  // Risks
  const riskKeys = Object.keys(race).filter(k => !isIdField(k) && k.toLowerCase().endsWith('risk'));
  riskKeys.forEach(k => rendered.add(k));
  if (riskKeys.length > 0) {
    blocks.push({ title: 'Risks', node: (
      <>{riskKeys.map(k => <FieldRow key={k} label={humanizeKey(k)} value={race[k]} />)}</>
    ) });
  }

  // Energy / Fuel / Tyres — energy sessions as summary rows, rest generic
  const { rows: energyRows, consumed: energyConsumed } = buildEnergyRows(race);
  energyConsumed.forEach(k => rendered.add(k));
  const fuelTyreKeys = Object.keys(race).filter(k =>
    !rendered.has(k) && !isIdField(k) && (k.startsWith('fuel') || k.startsWith('tyre') || k.startsWith('energy')));
  fuelTyreKeys.forEach(k => rendered.add(k));
  if (energyRows.length > 0 || fuelTyreKeys.length > 0) {
    blocks.push({ title: 'Energy / Fuel / Tyres', node: (
      <>
        {energyRows.map(row => <EnergyRow key={row.label} row={row} />)}
        {fuelTyreKeys.map(k => <FieldRow key={k} label={humanizeKey(k)} value={race[k]} />)}
      </>
    ) });
  }

  // Qualifying
  const qualKeys = Object.keys(race).filter(k =>
    !rendered.has(k) && !isIdField(k) && (k.startsWith('q1') || k.startsWith('q2') || k.toLowerCase().includes('tcd')));
  qualKeys.forEach(k => rendered.add(k));
  if (qualKeys.length > 0) {
    blocks.push({ title: 'Qualifying', node: (
      <>{qualKeys.map(k => <FieldRow key={k} label={humanizeKey(k)} value={race[k]} />)}</>
    ) });
  }

  // Everything else
  const otherKeys = Object.keys(race).filter(k => !rendered.has(k) && !isIdField(k));
  if (otherKeys.length > 0) {
    blocks.push({ title: 'Other', node: (
      <>{otherKeys.map(k => <FieldRow key={k} label={humanizeKey(k)} value={race[k]} />)}</>
    ) });
  }

  return blocks;
};

// ── Sort options for the summary table ─────────────────────────────────────

const SORT_OPTIONS = [
  { key: 'overall', label: 'Overall', get: r => r.race?.driverOverall },
  { key: 'ct', label: 'CT', get: r => r.race?.clearDryRisk },
  { key: 'consumption', label: 'Energy used', get: r => {
    const { energyRaceFrom, energyRaceTo } = r.race || {};
    return (energyRaceFrom != null && energyRaceTo != null) ? energyRaceFrom - energyRaceTo : null;
  } },
];

const carLevel = (race) => {
  const { carPower: p, carHandling: h, carAcceleration: a } = race || {};
  if (p == null || h == null || a == null) return null;
  return Math.round(((p + h + a) / 39) * 100) / 100;
};

const SimilarRacesModal = ({ trackId, trackName, onClose }) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('overall');
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/track/${trackId}/similar-races`);
        if (!cancelled) setRows(res.data || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.status === 403 ? 'Access denied.' : 'Failed to load.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [trackId]);

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sorted = React.useMemo(() => {
    if (!rows) return [];
    const opt = SORT_OPTIONS.find(o => o.key === sortKey);
    const withVal = rows.map((r, i) => ({ r, i, v: opt.get(r) }));
    withVal.sort((a, b) => {
      if (a.v == null && b.v == null) return 0;
      if (a.v == null) return 1;
      if (b.v == null) return -1;
      return sortDir === 'desc' ? b.v - a.v : a.v - b.v;
    });
    return withVal.map(x => ({ ...x.r, _origIndex: x.i }));
  }, [rows, sortKey, sortDir]);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 'var(--radius-lg)',
        padding: 24, width: 900, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Similar races — {trackName ?? `Track #${trackId}`}</div>
            <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginTop: 2 }}>
              Historical races on this track across all managers. Click a row for full details.
            </div>
          </div>
          <button onClick={onClose} style={{
            padding: '6px 14px', borderRadius: 'var(--radius-sm)',
            background: '#f9fafb', border: '1px solid var(--c-border)',
            color: 'var(--c-text-muted)', cursor: 'pointer', fontSize: 13,
          }}>Close</button>
        </div>

        {loading && <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>}
        {!loading && error && <div style={{ color: 'var(--c-error)', fontSize: 13 }}>{error}</div>}
        {!loading && !error && sorted.length === 0 && (
          <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>No historical races found for this track.</div>
        )}

        {/* No fb-table-wrap here on purpose: that class sets overflow-x:auto,
            which per the CSS overflow spec forces overflow-y to compute as
            auto too — that would hijack position:sticky away from this
            modal's own scrolling box (the div above). Columns here are few
            enough to not need horizontal scroll. */}
        {!loading && !error && sorted.length > 0 && (
          <table className="fb-table fb-table-sm ra-table">
            <thead>
              <tr>
                <th>Manager</th>
                <th>Season / Race</th>
                {SORT_OPTIONS.map(o => (
                  <th key={o.key} style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(o.key)}>
                    {o.label}{sortKey === o.key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
                  </th>
                ))}
                <th>Car Level</th>
                <th>P / H / A</th>
                <th>Energy (start→finish)</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => {
                const race = row.race || {};
                const consumption = (race.energyRaceFrom != null && race.energyRaceTo != null)
                  ? (race.energyRaceFrom - race.energyRaceTo) : null;
                const isExpanded = expanded === row._origIndex;
                return (
                  <React.Fragment key={row._origIndex}>
                    <tr
                      style={{
                        cursor: 'pointer', opacity: race.isAnomaly ? 0.55 : 1,
                        ...(isExpanded ? {
                          position: 'sticky', top: 29, zIndex: 2, background: '#fff',
                          boxShadow: '0 1px 0 var(--c-border)',
                        } : {}),
                      }}
                      title={race.isAnomaly ? (race.anomalyReason || 'Anomalous race') : undefined}
                      onClick={() => setExpanded(isExpanded ? null : row._origIndex)}
                    >
                      <td>{row.managerName ?? '—'}{race.isAnomaly && <span style={{ marginLeft: 4, fontSize: 10 }}>⚠</span>}</td>
                      <td style={{ fontSize: 12 }}>S{race.season} R{race.raceNumber}</td>
                      <td>{race.driverOverall ?? '—'}</td>
                      <td>{race.clearDryRisk ?? '—'}</td>
                      <td>{consumption != null ? consumption : '—'}</td>
                      <td>{carLevel(race) ?? '—'}</td>
                      <td style={{ fontSize: 12 }}>{race.carPower ?? '—'} / {race.carHandling ?? '—'} / {race.carAcceleration ?? '—'}</td>
                      <td style={{ fontSize: 12 }}>{race.energyRaceFrom ?? '—'} → {race.energyRaceTo ?? '—'}</td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} style={{ background: '#fafafa', padding: 14 }}>
                          <div className="sr-detail-columns">
                            {buildDetailBlocks(race).map(({ title, node }) => (
                              <DetailBlock key={title} title={title}>{node}</DetailBlock>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SimilarRacesModal;
